import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, Check } from "lucide-react";
import { toast } from "sonner";
import { CONFIG } from "@/lib/data";
import { submitEnquiry } from "@/lib/api";

const SLOTS = ["10:00 AM", "12:30 PM", "3:00 PM", "5:30 PM"];

export const BookingDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    setLoading(true);
    try {
      await submitEnquiry({
        name: form.name, email: form.email, phone: form.phone,
        service: "Discovery Call",
        message: `Requested call on ${form.date || "any day"} at ${form.time || "any time"}. ${form.message}`,
        kind: "booking",
      });
      setDone(true);
      toast.success("Call requested — we'll confirm by email shortly.");
    } catch {
      toast.error("Something went wrong. Try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => { onClose(); setTimeout(() => setDone(false), 300); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 z-[95] grid place-items-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg my-auto rounded-3xl glass p-7 sm:p-9"
            data-testid="booking-dialog"
          >
            <button onClick={close} data-testid="booking-dialog-close" aria-label="Close"
                    className="absolute top-4 right-4 grid h-9 w-9 place-items-center rounded-full glass hover:border-[var(--border-hover)]">
              <X size={16} />
            </button>

            {done ? (
              <div className="py-10 text-center">
                <motion.span
                  initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}
                  className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full btn-accent">
                  <Check size={30} />
                </motion.span>
                <h3 className="font-display text-2xl font-bold mb-2">Request received</h3>
                <p className="font-body text-[var(--text-secondary)]">We'll email you to lock the exact slot. Talk soon.</p>
                <button onClick={close} className="btn-accent mt-7 rounded-full px-7 py-3 font-heading text-sm font-semibold">Done</button>
              </div>
            ) : CONFIG.calendlyUrl ? (
              <div>
                <h3 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><Calendar size={22} /> Book a Call</h3>
                <iframe title="Calendly" src={CONFIG.calendlyUrl} className="w-full h-[520px] rounded-xl border-0" />
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <h3 className="font-display text-2xl font-bold flex items-center gap-2"><Calendar size={22} className="text-[var(--accent-primary)]" /> Book a Discovery Call</h3>
                  <p className="font-body text-sm text-[var(--text-secondary)] mt-1">Pick a preferred slot — we'll confirm by email.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="Full name" className="field" />
                  <input data-testid="booking-email" value={form.email} onChange={set("email")} type="email" placeholder="Email" className="field" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className="field" />
                  <input value={form.date} onChange={set("date")} type="date" className="field" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {SLOTS.map((s) => (
                    <button type="button" key={s} onClick={() => setForm((f) => ({ ...f, time: s }))}
                            className={`rounded-full px-4 py-2 text-sm transition-all ${form.time === s ? "btn-accent" : "border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-hover)]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
                <textarea value={form.message} onChange={set("message")} placeholder="What would you like to discuss?" rows={3} className="field resize-none" />
                <button type="submit" disabled={loading} data-testid="booking-submit"
                        className="btn-accent w-full rounded-full py-4 font-heading font-semibold disabled:opacity-60">
                  {loading ? "Sending..." : "Request Call"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
